import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axiosInstance from "../../interceptor/axiosInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faThumbsUp, faThumbsDown } from "@fortawesome/free-solid-svg-icons";
import { formatTime } from '../Utils/UtilityService.js'
import { clearGlobalInterval } from "../Utils/IntervalManager.js";
import config from "../../config.js";
import "./Org.css"
import "./Chat.css";
import NotificationService from "../Utils/NotificationService.js";
import EditDialog from "./EditDialog";
 
const Chat = () => {
    const location = useLocation();
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const urlParams = new URLSearchParams(search);
    const threadIdFromURL = urlParams.get('thread_id') || urlParams.get('chatId');
    const [selectedChatType, setSelectedChatType] = useState(threadIdFromURL ? 'all-chats' : 'my-chats');

    const [chatList, setChatList] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newMessage, setNewMessage] = useState("");
    const [setIsChatClose] = useState(false);
    const messagesEndRef = useRef(null);
    const [ws, setWs] = useState(null);
    const selectedChatRef = useRef(null); 
    const orgIdRef = useRef(null); 
    const selectedChatTypeRef = useRef(null); 
    const [orgId, setOrgId] = useState(null);
    const [setUserType] = useState(null);
    const [openChatList, setOpenChatList] = useState([]); 
    const [hover, setHover] = useState(false);
    const [openChatTypes] = useState([{short: 'WAIT', name: 'Waiting'}, {short: 'BOT_CHAT', name: 'Chat With Bot'}]);
    const [selectedOpenChatType, setSelectedOpenChatType] = useState(openChatTypes.map((o)=> o.short));
    const [myChatTypes] = useState([{short: 'ACT', name: 'Active'}, {short: 'CLS', name: 'Close'}]);
    const [selectedMyChatType, setSelectedMyChatType] = useState(myChatTypes.map((o)=> o.short));
    const [allChatTypes] = useState([
        {short: 'ACT', name: 'Active'}, 
        {short: 'WAIT', name: 'Waiting'}, 
        {short: 'AS_CLS', name: 'Assign Close'},
        {short: 'UNS_CLS', name: 'Unassign Close'}, 
        {short: 'BOT_CHAT', name: 'Chat With Bot'}]);
    const [selectedAllChatType, setSelectedAllChatType] = useState(allChatTypes.map((o)=> o.short));
    const [assignedToMeThreadId, setAssignedToMeThreadId] = useState(null);
    const [activeTab, setActiveTab] = useState(threadIdFromURL ? "all-chats" : "my-chats"); 
    const [editDialog, setEditDialog] = useState(false);
    const initialThreadIdRef = useRef(threadIdFromURL);
    const [selectedMessage, setSelectedMessage] = useState(null);

    // Update ref whenever selectedTicket changes
    useEffect(() => {
        selectedChatRef.current = selectedChat;
        orgIdRef.current = orgId;
        selectedChatTypeRef.current = selectedChatType;
    }, [selectedChat, orgId, selectedChatType]);

    useEffect(() => {
        const socket = new WebSocket(config.REACT_APP_WEBSOCKET_URL);
        setWs(socket);
    
        socket.onmessage = async (event) => {
            if (event.data instanceof Blob) {
                const textData = await event.data.text();
                const resData = JSON.parse(textData);
                const text = resData.content;
        
                // Handle feedback separately
                if (resData.type === 'feedback') {
                    console.log('Received feedback:', resData.feedback, 'for messageId:', resData.messageId);
        
                    setMessages((prevMessages) =>
                        prevMessages.map((msg) =>
                            msg.message_id === resData.messageId
                                ? { ...msg, feedback: resData.feedback }
                                : msg
                        )
                    );
                    return;
                }
        
                // Handle new messages
                if (selectedChatRef.current && resData.threadId === selectedChatRef.current.thread_id) {
                    if (resData.chatStatus === "closed") {
                        fetchChats(false);
                    }
                    
                    setMessages((prev) => {
                        const exists = prev.some(msg => msg.message_id === resData.message_id);
                        if (exists) return prev; // Prevent duplicate
                        return [
                            ...prev,
                            {
                                message_id: resData.message_id,
                                message: text,
                                sender: resData.sender,
                                created_at: resData.created_at,
                                chat_id: resData.chat_id,
                                ticket_id: null,
                                user_id: null
                            }
                        ];
                    });
                    
        
                    if (orgIdRef.current === resData.orgId) {
                        if (selectedChatTypeRef.current === "open-chats") {
                            fetchOpenChats(false, true);
                        } else if (selectedChatTypeRef.current === "all-chats") {
                            fetchAllChats(false, true);
                        }
                    }
                } else if (orgIdRef.current === resData.orgId) {
                    if (selectedChatTypeRef.current === "open-chats") {
                        fetchOpenChats(false, true);
                    } else if (selectedChatTypeRef.current === "all-chats") {
                        fetchAllChats(false, true);
                    }
                }
            }
        };        
    
        socket.onerror = (error) => {
            console.error('WebSocket error:', error);
        };
    
        socket.onclose = () => {
            console.log('WebSocket connection closed');
        };
    
        // Cleanup on unmount
        return () => {
            if (socket && socket.readyState === WebSocket.OPEN) {
                socket.close();
            }
        };
    }, []);
    
    const LiveTime = ({ timestamp }) => {
        const [formattedTime, setFormattedTime] = useState(formatTime(timestamp));
    
        useEffect(() => {
            const interval = setInterval(() => {
                setFormattedTime(formatTime(timestamp));
            }, 60000); // Update every 60 seconds
    
            return () => clearInterval(interval); // Cleanup interval
        }, [timestamp]);
    
        return <span>{formattedTime}</span>;
    };

    const FormatDate = ({ datetime }) => {
        const date = new Date(datetime);
        const formattedDate = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
        });
      
        return <span>{formattedDate}</span>;
      };

    const fetchChats = async (chatType) => {
        try {
            const response = await axiosInstance.get(`/api/ticket/list`, {params: {chatTypes: selectedMyChatType}});
            setChatList(response.data);
            if(assignedToMeThreadId !=null){
                const resData = response.data.filter((chat) => chat.thread_id === assignedToMeThreadId);
                setSelectedChat(resData[0]);
            }else{
                setSelectedChat(response.data[0]);
            }
            setAssignedToMeThreadId(null);
            
        } catch (err) {
            setError("Error fetching data");
        } finally {
            setLoading(false);
        }
    };

    // const fetchOpenChats = async (setSelected = true, skipLoader = false) => {
    //     try {
    //         // const user = JSON.parse(localStorage.getItem("user"));
    //         // if (user?.role) {
    //         //     setUserType(user.role);
    //         // }
    //         const response = await axiosInstance.get("/api/ticket/open-ticket-list", {params: {openChatTypes: selectedOpenChatType}, skipLoader});
    //         setOpenChatList(response.data);
    //         if(setSelected || response.data.length==1){
    //         setSelectedChat(response.data[0]);
    //         }
    //     } catch (err) {
    //         setError("Error fetching data");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const fetchOpenChats = async (setSelected = true, skipLoader = false) => {
        try {
            const response = await axiosInstance.get("/api/ticket/open-ticket-list", {
                params: { openChatTypes: selectedOpenChatType },
                skipLoader
            });
    
            setOpenChatList(response.data);
    
            const updatedSelected = response.data.find(
                chat => chat.thread_id === selectedChatRef.current?.thread_id
            );
    
            if (setSelected || response.data.length === 1 || updatedSelected) {
                setSelectedChat(updatedSelected || response.data[0]);
            }
        } catch (err) {
            setError("Error fetching data");
        } finally {
            setLoading(false);
        }
    };

    
    // const fetchAllChats = async (setSelected = true,skipLoader = false) => {
    //     try {
    //         const response = await axiosInstance.get(`/api/ticket/get-all-chats`, {params: {allChatTypes: selectedAllChatType}, skipLoader});
    //         if(response.data) {
    //             setChatList(response.data);
    //             if(setSelected || response.data.length==1){
    //             setSelectedChat(response.data[0]);
    //             }
    //         } else {
    //             setChatList([]);
    //             setSelectedChat(null);
    //         }
            
    //     } catch (err) {
    //         setError("Error fetching data");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // const fetchAllChats = async (setSelected = true, skipLoader = false) => {
    //     try {
    //         const response = await axiosInstance.get(`/api/ticket/get-all-chats`, {
    //             params: { allChatTypes: selectedAllChatType },
    //             skipLoader
    //         });
    
    //         if (response.data) {
    //             setChatList(response.data);
    
    //             if (setSelected || response.data.length === 1) {
    //                 const updatedSelected = response.data.find(
    //                     chat => chat.thread_id === selectedChat?.thread_id
    //                 );
    //                 setSelectedChat(updatedSelected || response.data[0]);
    //             }
    //         } else {
    //             setChatList([]);
    //             setSelectedChat(null);
    //         }
    //     } catch (err) {
    //         setError("Error fetching data");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const fetchAllChats = async (setSelected = true, skipLoader = false) => {
        try {
            const response = await axiosInstance.get(`/api/ticket/get-all-chats`, {
                params: { allChatTypes: selectedAllChatType },
                skipLoader,
            });
    
            if (response.data) {
                fetchOrgDetails();
                setChatList(response.data);
    
                // Prefer selecting by initial thread id if provided via URL
                let preferredSelected = null;
                if (initialThreadIdRef.current) {
                    preferredSelected = response.data.find(chat => String(chat.thread_id) === String(initialThreadIdRef.current));
                }

                // Try to find the updated version of the currently selected chat
                const updatedSelected = response.data.find(
                    chat => chat.thread_id === selectedChatRef.current?.thread_id
                );
    
                if (preferredSelected || setSelected || response.data.length === 1 || updatedSelected) {
                    setSelectedChat(preferredSelected || updatedSelected || response.data[0]);
                    // Clear the one-time initial selection once applied
                    if (preferredSelected) {
                        initialThreadIdRef.current = null;
                    }
                }                
            } else {
                setChatList([]);
                setSelectedChat(null);
            }
        } catch (err) {
            setError("Error fetching data");
        } finally {
            setLoading(false);
        }
    };
    
    
    const fetchOrgDetails = async () => {
        try {
          const response = await axiosInstance.get(`/org/org-details`);
          const data = response.data[0];  // Assuming the response contains org data
          setOrgId(data.org_id);  
        } catch (error) {
          console.error('Error fetching organization details:', error);
        }
      };

    // Fetch data from the API when the component mounts
    useEffect(() => {
        // fetchChats();
        // fetchOpenChats();
        if (selectedChatType === "my-chats") {
            fetchOrgDetails();
            fetchChats("my-chats");
            // setSelectedChat(chatList.length > 0 ? chatList[0] : null)
        } else if (selectedChatType === "open-chats") {
            fetchOpenChats();
            // setSelectedChat(openChatList.length > 0 ? openChatList[0] : null)
        } else if (selectedChatType === "all-chats") {
            fetchAllChats();
        }

    }, [selectedChatType]);

    useEffect(() => {
        // fetchChats();
        if(selectedChatType === 'open-chats') {
            fetchOpenChats();
        }
    }, [selectedOpenChatType]);

    useEffect(() => {
        // fetchChats();
        if(selectedChatType === 'my-chats') {
            fetchChats();
        }
    }, [selectedMyChatType]);

    useEffect(() => {
        // fetchChats();
        if(selectedChatType === 'all-chats') {
            fetchAllChats();
        }
    }, [selectedAllChatType]);
    
    // Fetch the messages for the selected ticket
    useEffect(() => {
        if (selectedChat) {
            // Clear the previous interval before setting a new one
            clearGlobalInterval();

            // Define the fetchMessages function
            const fetchMessages = async (skipLoader = false) => {
                try {
                    const response = await axiosInstance.get(
                        `/api/ticket/get-conversations?thread_id=${selectedChat.thread_id}`,
                        { skipLoader }, // Pass the skipLoader flag in Axios config
                        
                    );
                    setMessages(response.data); // Set the messages for the selected ticket
                } catch (err) {
                    setError("Error fetching messages"); // Handle error if request fails
                }
            };

            // First immediate fetch
            fetchMessages(false);

            // If the ticket is not closed, set the interval for subsequent calls
            if (selectedChat.status !== "closed") {
                /* const intervalId = setInterval(() => {
                  fetchMessages(true); // Skip loader for interval-based requests
                }, 3000); // Set interval to 3 seconds
                setGlobalInterval(intervalId); // Store the interval globally */
            }
        }

        // Cleanup the interval when selectedTicket changes or component unmounts
        return () => {
            clearGlobalInterval();
        };
    }, [selectedChat]); // This effect depends on selectedTicket

    // Scroll to the bottom of the messages container whenever messages are updated
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const handleSendMessage = async () => {
        if (newMessage.trim()) {
            //Send WebSocket Message
            const payload = {
                chatId: selectedChat.thread_id,
                messages: newMessage.trim(),
            };
            try {
                const response = await axiosInstance.post(`/api/ticket/save-message`, payload, { skipLoader: true } );
                if (response.data && response.data.success) {
                setMessages([
                    ...messages,
                    {
                        sender: "agent",
                        message: newMessage.trim(),
                        created_at: new Date(),
                    },
                ]);
                if (ws) {
                    ws.send(JSON.stringify({ type: 'message', content: newMessage, threadId: selectedChat.thread_id }));
                }
            }else {
                      NotificationService.error(response.data.message || "Message send failed. Please try again.");
                    }
            } catch (err) {
                setError("Error to save message");
            }
            setNewMessage("");
        }
    };

    // Handle the Enter key press for sending a message
    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault(); // Prevent default Enter behavior (new line)
            handleSendMessage(); // Send the message
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    const handleChatClick = (ticket) => {
        // setIsChatClose(false);
        setSelectedChat(ticket);
    };

    const handleCloseChat = () => {
        const closeChat = async () => {
            try {
                const response = await axiosInstance.get(
                    `/agent-conversation/close?thread_id=` + selectedChat.thread_id
                );
                chatList.map((chat) => {
                    if (chat.thread_id === selectedChat.thread_id) {
                        chat.status = "closed"
                    }
                })
                // chatList = chatList.filter((e) => e.thread_id !== selectedChat.thread_id)
                setMessages([
                    ...messages,
                    {
                        sender: "agent",
                        message: response.data.message.message,
                        created_at: response.data.message.created_at,
                    },
                ]);
                if (ws) {
                    ws.send(JSON.stringify({ type: 'message', ticketStatus: "closed", content: response.data.message.message, threadId: selectedChat.thread_id }));
                }
            } catch (err) {
                setError("Error to close the ticket");
            }
        };
        closeChat();
    };

    const handleAssignChat = () => {
        const assignChat = async () => {
            try {
                const payload = { thread_id: selectedChat.thread_id };
                const response = await axiosInstance.post(`/agent-conversation/assign-to-agent`, payload);
    
                // Store the thread id to be auto-selected later
                setAssignedToMeThreadId(selectedChat.thread_id);
    
                // Send WebSocket message FIRST — before changing tabs
                if (ws) {
                    ws.send(JSON.stringify({ 
                        type: 'assign',
                        ticketStatus: "assigned",
                        content: response.data.message,
                        threadId: selectedChat.thread_id,
                        orgId: orgId 
                    }));
                }
    
                // Then change UI states
                setSelectedChatType("my-chats");
                setActiveTab("my-chats");
    
                // Delay slightly to ensure tab change propagates before fetch
                setTimeout(() => {
                    fetchChats("my-chats");  // explicitly use "my-chats"
                }, 100); // even 50ms might be enough
    
            } catch (err) {
                setError("Error assigning the chat");
            }
        };
        assignChat();
    };
    

    const statuses = ["Active", "Close"];
    const getStatusText = (ticket) => {
        if (ticket === "Active") return { text: "Active", color: "#c6fdc4" };
        return { text: "Closed", color: "#C9C9C9" };
    };

    const openChatStatuses = ["Waiting",  "Chat With Bot"];
    const getOpenChatStatusText = (ticket) => {
        if (ticket === "Waiting") return { text: "Waiting / Agent", color: "#f5c1ba" };
        return { text: "Chat with Bot", color: "#f4f49b" };
    };

    const allChatStatuses = ["Active", "Waiting", "Assign Close", "Unassign Close", "Chat With Bot"];
    const getAllChatStatusText = (ticket) => {
        if (ticket === "Active") return { text: "Active", color: "#c6fdc4" };
        if (ticket === "Waiting") return { text: "Waiting / Agent", color: "#f5c1ba" };
        if (ticket === "Assign Close") return { text: "Assign Closed", color: "#C9C9C9" };
        if (ticket === "Unassign Close") return { text: "Unassign Closed", color: "#bbad8d" };
        return { text: "Chat with Bot", color: "#f4f49b" };
    };

    const handleChangeChat = (event) => {
        setSelectedChatType(event.target.value);
        if (event.target.value === "my-chats") {
            setSelectedMyChatType(myChatTypes.map((o)=> o.short));
        } else if (event.target.value === "open-chats") {
            setSelectedOpenChatType(openChatTypes.map((o)=> o.short));
        } else if (event.target.value === "all-chats") {
            setSelectedAllChatType(allChatTypes.map((o)=> o.short))                                
        }
    };

    const handleCheckboxChangeForOpenChat = async (shortName) => {
        setSelectedOpenChatType((prevSelected) => {
          if (prevSelected.includes(shortName)) {
            // Uncheck logic
            const updatedSelection = prevSelected.filter((short) => short !== shortName);
            return updatedSelection;
          } else {
            // Check logic
            const updatedSelection = [...prevSelected, shortName];
            return updatedSelection;
          }
        });
    };

    const handleCheckboxChangeForMyChat = async (shortName) => {
        setSelectedMyChatType((prevSelected) => {
          if (prevSelected.includes(shortName)) {
            // Uncheck logic
            const updatedSelection = prevSelected.filter((short) => short !== shortName);
            return updatedSelection;
          } else {
            // Check logic
            const updatedSelection = [...prevSelected, shortName];
            return updatedSelection;
          }
        });
    };

    
    const handleCheckboxChangeForAllChat = async (shortName) => {
        setSelectedAllChatType((prevSelected) => {
          if (prevSelected.includes(shortName)) {
            // Uncheck logic
            const updatedSelection = prevSelected.filter((short) => short !== shortName);
            return updatedSelection;
          } else {
            // Check logic
            const updatedSelection = [...prevSelected, shortName];
            return updatedSelection;
          }
        });
        console.log(selectedAllChatType)
    };

    const getChatClass = (chat, selectedChat) => {
        let cs  = "";
        
      
        if (chat.is_my_chat && chat.is_my_chat === 1) {
          if (chat.chatStatus === "active" && chat.status === "assigned") {
            cs = "assigned-color";
          }
          if (chat.chatStatus === "closed" && chat.status === "closed") {
            cs = "assigned-and-closed-color";
          }
        }
      
        if (chat.is_open_chat && chat.is_open_chat === 1) {
          if (chat.status === "pending") {
            if (chat.waitingStatus === "waiting") {
                cs =  "waiting-color";
            }
            if (chat.waitingStatus === null) {
                cs =  "bot-chat-color";
            }
          }
          if (chat.status === "closed" && (chat.waitingStatus === "closed" || chat.waitingStatus === null)) {
            cs =  "unassigned-close-color";
          }
        }

        if (selectedChat?.thread_id === chat.thread_id) {
            return "selected-focus " + cs;
        } else {
            return "font-14 " + cs;
        }
        // return "";
      };
    
    

      const capitalizeFirst = (str) => {
        if (!str) return ""; // Handle empty input
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
      };

      const MessageCard = ({ chat, selectedChat}) => {

        const cardRef = useRef(null);
        const isSelected = selectedChat?.thread_id === chat.thread_id;

        useEffect(() => {
            if (isSelected && cardRef.current) {
            cardRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center", // or "nearest"
            });
            }
        }, [isSelected]);

          return (<div 
            ref={cardRef}
            key={chat.id}
            
              className={`chat-item 
                ${getChatClass(chat, selectedChat)}
                `}
              onClick={() => handleChatClick(chat)}
              style={{ cursor: "pointer", borderRadius: '0px' }}> 
      
             <div className="d-flex justify-content-between align-items-start border p-3 rounded shadow-sm">
              <div className="d-flex align-items-start ticket-details-style">
                <div style={{marginLeft: '6px'}}>
                  <div className="details-font-value">{chat.clientName}</div>
                  <div className="details-font-value">{chat.message}</div>                                        
                </div>
              </div>
        
              <div className="text-end">
                <div className="details-font-value date-text"><FormatDate  datetime={chat.created_at} /></div>
                <div className="status-container"> {chat.is_open_chat?(chat.status == 'pending' && chat.waitingStatus == null?'Chat With Bot': (capitalizeFirst(chat.status))): (chat.is_my_chat ? capitalizeFirst(chat.chatStatus) : capitalizeFirst(chat.status))}</div>
              </div>
            </div>
            </div>
          );
        };

    return (
        <div className="d-flex flex-column min-vh-86">
            <main className="flex-fill bg-light">
                <div className={`d-flex min-vh-86 ${selectedChat ? "has-selected-chat" : ""}`}>

                    <div className="chat-list-container chat-conv-bg">
                        <div style={styles.chatTypeHeader} className="chat-ticket-header">
                            {/* <select id="dropdown" value={selectedChatType} onChange={handleChangeChat}>
                                <option value="my-chats">My Chats</option>
                                <option value="open-chats">Open Chats</option>
                            </select> */}
                           {/*  <div style={styles.grid} className="mb-2">
                                <h5>Chats</h5>
                                <select
                                    id="dropdown"
                                    value={selectedChatType}
                                    onChange={handleChangeChat}
                                    style={{ ...styles.dropdown, ...(hover ? styles.dropdownHover : {}) }}
                                    className="dropdown-color"
                                    onMouseEnter={() => setHover(true)}
                                    onMouseLeave={() => setHover(false)}
                                    >
                                    <option value="my-chats">My Chats</option>
                                    <option value="open-chats">Open Chats</option>
                                    <option value="all-chats">All Chats</option>
                                </select>
                            </div> */}
                            <div >
                                <div className="d-flex border-bottom-grey" style={{marginRight: '0px'}}>
                                <div
                                    className={`tab-item ${activeTab === "my-chats" ? "active-tab" : ""}`}
                                    onClick={() => {setActiveTab("my-chats"); setSelectedChatType("my-chats"); setSelectedMyChatType(myChatTypes.map((o)=> o.short))}}
                                >
                                    My Chats
                                </div>
                                <div
                                    className={`tab-item ${activeTab === "open-chats" ? "active-tab" : ""}`}
                                    onClick={() => {setActiveTab("open-chats"); setSelectedChatType("open-chats");setSelectedOpenChatType(openChatTypes.map((o)=> o.short))}}
                                >
                                    Open Chats
                                </div>
                                <div
                                    className={`tab-item ${activeTab === "all-chats" ? "active-tab" : ""}`}
                                    onClick={() => {setActiveTab("all-chats");setSelectedChatType("all-chats"); setSelectedAllChatType(allChatTypes.map((o)=> o.short))}}
                                >
                                    All Chats
                                </div>
                                </div>
                            </div>
                            
                            {selectedChatType === "open-chats" ? (
                                <div style={styles.grid} className="mt-2">
                                {openChatTypes.map((ticketType) => (
                                  <div key={ticketType.short} className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                    //   id={ticketType.short}
                                      checked={selectedOpenChatType.includes(ticketType.short)}
                                      onChange={() => handleCheckboxChangeForOpenChat(ticketType.short)}
                                    />
                                    <label className="form-check-label" htmlFor={ticketType.short}>
                                      {ticketType.name} &nbsp;
                                      {openChatStatuses.includes(ticketType.name) && (
                                    (() => {
                                        const { text, color } = getOpenChatStatusText(ticketType.name);
                                        return (
                                            <span
                                            style={{
                                                ...styles.statusBullet,
                                                backgroundColor: color, ...styles.statusItem,
                                            }}
                                            ></span>
                                        );
                                    })()
                                    )}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            ): (selectedChatType === "my-chats"? (
                                <div style={styles.grid} className="mt-2">
                                {myChatTypes.map((chatType) => (
                                  <div key={chatType.short} className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={selectedMyChatType.includes(chatType.short)}
                                      onChange={() => handleCheckboxChangeForMyChat(chatType.short)}
                                    />
                                    <label className="form-check-label" htmlFor={chatType.short}>
                                      {chatType.name}&nbsp;
                                    
                                    {statuses.includes(chatType.name) && (
                                    (() => {
                                        const { text, color } = getStatusText(chatType.name);
                                        return (
                                            <span
                                            style={{
                                                ...styles.statusBullet,
                                                backgroundColor: color, ...styles.statusItem,
                                            }}
                                            ></span>
                                        );
                                    })()
                                    )}</label>
                                  </div>
                                ))}
                              </div>
                            ): (
                                <div style={styles.grid} className="mt-2">
                                {allChatTypes.map((chatType) => (
                                  <div key={chatType.short} className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                    //   id={ticketType.short}
                                      checked={selectedAllChatType.includes(chatType.short)}
                                      onChange={() => handleCheckboxChangeForAllChat(chatType.short)}
                                    />
                                    <label className="form-check-label" htmlFor={chatType.short}>
                                        {chatType.name}&nbsp;
                                        {allChatStatuses.includes(chatType.name) && (
                                        (() => {
                                            const { text, color } = getAllChatStatusText(chatType.name);
                                            return (
                                                <span
                                                style={{
                                                    ...styles.statusBullet,
                                                    backgroundColor: color, ...styles.statusItem,
                                                }}
                                                ></span>
                                            );
                                        })()
                                        )}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            ))
                            }
                        </div>

                        <div style={styles.chatList} className={`chat-conv-bg ${selectedChatType==="all-chats"? "all-chat-height": "common-chat-height"}`}>
                            {selectedChatType === "my-chats" || selectedChatType === "all-chats" ? (<div>
                                {chatList.length === 0 && (<p style={styles.noChats}>Chats not available </p>)}
                                {/* <ul className="list-display">
                                    {chatList.map((chat, index) => (
                                        <li
                                            key={index}
                                            className={`chat-item 
                                                ${getChatClass(chat, selectedChat)}
                                                `}
                                            onClick={() => handleChatClick(chat)}
                                        >
                                            {chat.message}
                                        </li>
                                    ))}
                                </ul> */}
                                {chatList.map((chat, index) => (
                                <MessageCard
                                    chat = {chat}
                                    selectedChat = {selectedChat}
                                />
                            ))}
                            </div>) : (<div>
                                {openChatList.length === 0 && (<p style={styles.noChats}>Chats not available </p>)}
                                {/* <ul className="list-display">
                                    {openChatList.map((chat, index) => (
                                         <li
                                            key={index.queue_id || `status-${index}`}
                                            style={{
                                                ...styles.row,
                                                ...(chat.waitingStatus === "waiting" && chat.status === "pending"
                                                    ? styles.waitingColor
                                                    : chat.status === "closed"
                                                        ? styles.closeColor
                                                        : styles.conversationColor),
                                            }}
                                            className={`chat-item
                                                ${selectedChat?.thread_id === chat.thread_id ? "selected-focus" : ""}
                                                `}
                                            onClick={() => handleChatClick(chat)}
                                        >
                                            {chat.message}
                                        </li> 
                                    ))}
                                </ul> */}
                                {openChatList.map((chat, index) => (
                                <MessageCard
                                    chat = {chat}
                                />
                            ))}
                            </div>)
                            }
                        </div>
                    </div>

                    <div className="chat-detail" style={styles.chatDetail}>
                        {selectedChatType === "my-chats" || selectedChatType === "all-chats" ? (<div>
                            {/* My chat details */}
                            {selectedChat ? (
                                <div style={styles.chatContainer}>
                                    <h4 style={styles.chatHeader} className="chat-detail-header">
                                        <button className="mobile-back-btn" onClick={() => setSelectedChat(null)}>
                                            ← Back
                                        </button>
                                        Conversation Details{" "}
                                        {(selectedChat.is_my_chat && selectedChat.is_my_chat === 1 && selectedChat.status !== "closed" && selectedChat.chatStatus !== "closed") && (
                                            <button
                                                className=""
                                                style={styles.closeButton}
                                                onClick={() => handleCloseChat()}
                                            >
                                                Mark as Closed
                                            </button>
                                        )}
                                        {(selectedChat.is_open_chat && selectedChat.is_open_chat === 1 && selectedChat.waitingStatus === "waiting" && selectedChat.status === "pending") && (
                                            <button
                                                style={styles.closeButton}
                                                onClick={() => handleAssignChat()}
                                            >
                                                Assign To Me
                                            </button>
                                        )}

                                    </h4>

                                    <div style={{ position: "relative", width: "100%", display: "flex", alignItems: "center" }}>
                                        <hr style={{ border: "none", height: "2px", backgroundColor: "grey", flex: 1 }} />
                                    </div>

                                    <div style={styles.messagesContainer} className="chat-conv-bg">
                                        {messages.length > 0 ? (
                                            messages.map((message, index) => (
                                                <div key={index} style={styles.messageContainer}>
                                                    <div
                                                        style={styles.messageName}
                                                        className={`${message.sender === "user"
                                                            ? "user-time"
                                                            : "support-time"
                                                            }`}
                                                    >
                                                        {message.sender === "user"
                                                            ? "client"
                                                            : message.sender === "bot"
                                                                ? "assistant"
                                                                : "you"} {", "}
                                                        {            <LiveTime timestamp={message.created_at} />
                                                    }
                                                    </div>
                                                    <div
                                                        className={`message ${message.sender === "user"
                                                            ? "user-message"
                                                            : "support-message"
                                                            }`}
                                                        style={styles.messageBox}
                                                    >
                                                        {message.message}
                                                    
                                                        {message.feedback && (
                                                        <div style={styles.rating}>
                                                            {message.feedback === "like" && (
                                                            <div
                                                                style={{
                                                                ...styles.icon,
                                                                color: "#115a22",    // green for like
                                                                pointerEvents: "none",
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faThumbsUp} size="lg" />
                                                            </div>
                                                            )}
                                                            {message.feedback === "dislike" && (
                                                            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                                                <button
                                                                    onClick={() => 
                                                                        {setSelectedMessage(message);
                                                                            setEditDialog(true);}}
                                                                    style={{
                                                                        ...styles.icon,
                                                                        color: "#b30000", // blue for edit
                                                                        background: "transparent",
                                                                        border: "none",
                                                                        cursor: "pointer",
                                                                        fontSize: "14px",
                                                                    }}
                                                                    >
                                                                    Edit Knowledge Base
                                                                    </button>
                                                                <div
                                                                    style={{
                                                                    ...styles.icon,
                                                                    color: "#b30000",    // red for dislike
                                                                    pointerEvents: "none",
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faThumbsDown} size="lg" />
                                                                </div>  
                                                            </div>
                                                                                                             
                                                            )}
                                                        </div>
                                                        )}

                                                        {message.foundAnswer === 0 && (
                                                            <div style={styles.rating}>
                                                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedMessage(message);
                                                                            setEditDialog(true);
                                                                        }}
                                                                        style={{
                                                                            ...styles.icon,
                                                                            color: "#b30000", // blue for edit
                                                                            background: "transparent",
                                                                            border: "none",
                                                                            cursor: "pointer",
                                                                            fontSize: "14px",
                                                                        }}
                                                                    >
                                                                        Edit Knowledge Base
                                                                    </button>
                                                                    <div
                                                                        style={{
                                                                            ...styles.icon,
                                                                            color: "#b30000",    // red for dislike
                                                                            pointerEvents: "none",
                                                                        }}
                                                                    ></div>

                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                </div>
                                                
                                            ))
                                            
                                        ) : (
                                            <div style={styles.noMessages}>No messages to display</div>
                                        )}
                                        {selectedMessage && (
                                            <EditDialog
                                                open={editDialog}
                                                onClose={() => setEditDialog(false)}
                                                onSave={() => {
                                                console.log("Saved changes for", selectedMessage);
                                                setEditDialog(false);
                                                }}
                                                chatId={selectedMessage.chat_id}
                                                messageId={selectedMessage.message_id}
                                                orgId={orgId}
                                            />
                                            )}
                                        <div ref={messagesEndRef} />
                                    </div>

                                    {selectedChat.is_my_chat && selectedChat.is_my_chat === 1 && selectedChat.status !== "closed" && selectedChat.chatStatus !== "closed" && (
                                        <div style={styles.messageInput}>
                                            <input
                                                type="text"
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                placeholder="Type a message..."
                                                style={styles.inputField}
                                                onKeyPress={handleKeyPress} // Handle Enter key press
                                            />
                                            <button onClick={handleSendMessage} style={styles.sendButton} className="send-button">
                                                <FontAwesomeIcon icon={faPaperPlane} /> Send
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={styles.noChats}>Select a ticket to view details</div>
                            )}
                        </div>) : (<div>
                            {/* Open chats details */}
                            {selectedChat ? (
                                <div style={styles.chatContainer} className="chat-conv-bg">
                                    <h4 style={styles.chatHeader} className="chat-ticket-header chat-detail-header">
                                        <button className="mobile-back-btn" onClick={() => setSelectedChat(null)}>
                                            ← Back
                                        </button>
                                        Conversation Details{" "}
                                        {(selectedChat.waitingStatus === "waiting" && selectedChat.status === "pending") && (
                                            <button
                                                style={styles.closeButton}
                                                onClick={() => handleAssignChat()}
                                            >
                                                Assign To Me
                                            </button>
                                        )}
                                    </h4>

                                    <div style={styles.messagesContainer}>
                                        {messages.length > 0 ? (
                                            messages.map((message, index) => (  
                                                <div key={index} style={styles.messageContainer}>
                                                    <div
                                                        style={styles.messageName}
                                                        className={`${message.sender === "user"
                                                            ? "user-time"
                                                            : "support-time"
                                                            }`}
                                                    >
                                                        {message.sender === "user"
                                                            ? "client"
                                                            : message.sender === "bot"
                                                                ? "assistant"
                                                                : "you"} {", "}
                                                        {            <LiveTime timestamp={message.created_at} />
                                                    }
                                                    </div>
                                                    <div
                                                        className={`message ${message.sender === "user"
                                                            ? "user-message"
                                                            : "support-message"
                                                            }`}
                                                        style={styles.messageBox}
                                                    >
                                                        {message.message}
                                                        {message.feedback && (
                                                        <div style={styles.rating}>
                                                            {message.feedback === "like" && (
                                                            <div
                                                                style={{
                                                                ...styles.icon,
                                                                color: "#115a22",    // green for like
                                                                pointerEvents: "none",
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faThumbsUp} size="lg" />
                                                            </div>
                                                            )}
                                                            {message.feedback === "dislike" && (
                                                            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                                                <button
                                                                    onClick={() => 
                                                                        {setSelectedMessage(message);
                                                                            setEditDialog(true);}}
                                                                    style={{
                                                                        ...styles.icon,
                                                                        color: "#b30000", // blue for edit
                                                                        background: "transparent",
                                                                        border: "none",
                                                                        cursor: "pointer",
                                                                        fontSize: "14px",
                                                                    }}
                                                                    >
                                                                    Edit Knowledge Base
                                                                    </button>
                                                                <div
                                                                    style={{
                                                                    ...styles.icon,
                                                                    color: "#b30000",    // red for dislike
                                                                    pointerEvents: "none",
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faThumbsDown} size="lg" />
                                                                </div>  
                                                            </div>
                                                            )}
                                                        </div>
                                                        )}

{message.foundAnswer === 0 && (
                                                            <div style={styles.rating}>
                                                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedMessage(message);
                                                                            setEditDialog(true);
                                                                        }}
                                                                        style={{
                                                                            ...styles.icon,
                                                                            color: "#b30000", // blue for edit
                                                                            background: "transparent",
                                                                            border: "none",
                                                                            cursor: "pointer",
                                                                            fontSize: "14px",
                                                                        }}
                                                                    >
                                                                        Edit Knowledge Base
                                                                    </button>
                                                                    <div
                                                                        style={{
                                                                            ...styles.icon,
                                                                            color: "#b30000",    // red for dislike
                                                                            pointerEvents: "none",
                                                                        }}
                                                                    ></div>

                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={styles.noMessages}>No messages to display</div>
                                        )}
                                         {selectedMessage && (
                                            <EditDialog
                                                open={editDialog}
                                                onClose={() => setEditDialog(false)}
                                                onSave={() => {
                                                console.log("Saved changes for", selectedMessage);
                                                setEditDialog(false);
                                                }}
                                                chatId={selectedMessage.chat_id}
                                                messageId={selectedMessage.message_id}
                                                orgId={orgId}
                                            />
                                            )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                </div>
                            ) : (
                                <div style={styles.noChats}>Select a ticket to view details</div>
                            )}
                        </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    )
};

const styles = {
    chatList: {
        padding: "5px",
        overflowY: 'auto',
    },
    chatDetail: {
        flex: 1,
        padding: "0px",
        background: "#fff",
    },
    chatTypeHeader: {
        position: "relative", // Allow absolute positioning for the button
        padding: "10px",
        // backgroundColor: "#9ABEB8",
        // borderBottom: "1px solid #ccc",
        // textAlign: "center",
        color: "white",
        borderRight: '1px solid rgba(128, 128, 128, 0.25)' 
    },
    chatContainer: {
        display: "flex",
        flexDirection: "column",
        height: "94vh", // Full viewport height
    },

    //like dislike button styles
    rating: {
        position: "absolute",
        bottom: "-21px",           // aligns it to bottom of the message
        right: "10px",           // aligns it to right of the message
        display: "flex",           
        zIndex: 1,
      },


    chatHeader: {
        position: "relative", // Allow absolute positioning for the button
        padding: "5px",
        // backgroundColor: "#1A4164",
        // borderBottom: "1px solid #ccc",
        // textAlign: "center",
        // color: "white",
        marginTop: '10px',
        marginLeft: '5px',
        marginBottom: '-5px'
    },
    closeButton: {
        position: "absolute", // Position button absolutely
        right: "10px", // Place it on the right
        top: "50%", // Vertically align it
        transform: "translateY(-50%)", // Center perfectly vertically
        padding: "5px 10px",
        backgroundColor: "#F5F6FA",
        border: "1px solid #A2A2A2",
        borderRadius: "4px",
        cursor: "pointer",
        color: "#8094AE",
        fontSize: "12px",
    },

    messagesContainer: {
        flex: 1, // Allow this section to grow and shrink
        overflowY: "auto", // Scroll if content overflows
        padding: "10px",
    },

    messageContainer: {
        marginBottom: "20px",
        position: "relative", // To position time below the box
    },

    messageName: {
        // position: "absolute",
        top: "-20px", // Name above the box
        left: "0",
        fontSize: "12px",
        fontWeight: "normal",
        color: "#888",
    },

    messageBox: {
        padding: "10px",
        borderRadius: "8px",
        maxWidth: "70%",
        wordWrap: "break-word",
        marginTop: "5px",
    },

    userMessage: {
        backgroundColor: "#D0E8FF",
        alignSelf: "flex-start",
    },
    supportMessage: {
        backgroundColor: "#A4E2B4",
        alignSelf: "flex-end",
    },

    messageTime: {
        fontSize: "12px",
        color: "#888",
        textAlign: "right", // Align time to the right
        marginTop: "5px",
    },

    messageInput: {
        padding: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "#f0f0f0",
    },

    inputField: {
        width: "85%",
        padding: "10px",
        fontSize: "14px",
        borderRadius: "4px",
        border: "1px solid #ccc",
    },

    sendButton: {
        width: "10%",
        padding: "10px",
        border: "none",
        cursor: "pointer",
    },

    noMessages: {
        textAlign: "center",
        fontStyle: "italic",
        padding: "20px",
    },
    noChats: {
        top: "-20px", // Name above the box
        left: "0",
        fontSize: "16px",
        fontWeight: "normal",
        color: "#888",
    },
    statusTextContainer: {
        position: "static",
        top: "65px",
        right: "30px",
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
    },
    statusItem: {
        display: "flex",
        alignItems: "center",
        margin: "5px",
        marginTop: "6px"
    },
    statusBullet: {
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        marginRight: "8px",
    },
    statusText: {
        fontSize: "12px",
    },

    waitingColor: {
        backgroundColor: "#f5c1ba",
    },
    closeColor: {
        backgroundColor: "#C9C9C9",
    },
    conversationColor: {
        backgroundColor: "#F4F49B",
    },

    dropdown: {
        padding: '5px',
        fontSize: '16px',
        borderRadius: '5px',
        // border: '1px solid #ccc',
        outline: 'none',
        cursor: 'pointer',
        // backgroundColor: '#f9f9f9',
        transition: 'all 0.3s ease',
        fontWeight: 'bold',
        width: '8rem'
    },
    dropdownHover: {
        backgroundColor: '#e0e0e0',
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)", // Two columns
    },

    statusContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        // gap: '16px'
    },
}
export default Chat;


